import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { groupsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Users, CheckCircle, AlertCircle } from 'lucide-react';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [status, setStatus] = useState('loading'); // loading | success | error | login_required
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('Invalid link. No invitation token found.');
      return;
    }
    if (!isAuthenticated()) {
      setStatus('login_required');
      setMessage('Please log in with the email address that received the invitation, then try again.');
      return;
    }
    if (authLoading) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await groupsAPI.acceptInvite(token);
        if (cancelled) return;
        if (response.data.success) {
          setStatus('success');
          setMessage(response.data.message || 'You have joined the group.');
          setTimeout(() => navigate('/dashboard'), 2000);
        } else {
          setStatus('error');
          setMessage(response.data.error || 'Could not accept invitation.');
        }
      } catch (error) {
        if (cancelled) return;
        const errMsg = error.response?.data?.error || error.message || 'Failed to accept invitation';
        setStatus('error');
        setMessage(errMsg);
      }
    })();
    return () => { cancelled = true; };
  }, [token, isAuthenticated, authLoading, navigate]);

  if (authLoading || (token && status === 'loading')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#459EA3] mx-auto"></div>
          <p className="mt-4 text-gray-600">Accepting invitation…</p>
        </div>
      </div>
    );
  }

  if (status === 'login_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-amber-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Log in to accept</h1>
          <p className="mt-2 text-gray-600 text-sm">{message}</p>
          <p className="mt-2 text-gray-500 text-sm">
            After logging in, come back to this link or open it again from the invitation email.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="/#/login"
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#459EA3] text-white font-semibold text-sm"
            >
              Go to login
            </a>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-300 text-gray-700 font-medium text-sm"
            >
              Back to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">Invitation could not be accepted</h1>
          <p className="mt-2 text-gray-600 text-sm">{message}</p>
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#459EA3] text-white font-semibold text-sm"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F5F7FA] p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-gray-200 p-8 text-center">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-7 h-7 text-green-600" />
        </div>
        <h1 className="text-xl font-bold text-gray-900">You're in the group</h1>
        <p className="mt-2 text-gray-600 text-sm">{message}</p>
        <p className="mt-4 text-gray-500 text-xs">You can now see scripts, agents, logs, and analysis shared by your group. Redirecting to dashboard…</p>
        <div className="mt-6 flex justify-center">
          <Users className="w-10 h-10 text-[#459EA3]/40" />
        </div>
      </div>
    </div>
  );
};

export default AcceptInvite;
